/**
 * O mapeamento vigente de estado do Caso para etapa do painel (`BR-006`).
 *
 * Mora no FRONT de propósito. A `BR-006` decidiu que a etapa é DERIVADA, e o
 * spec fala em "mapeamento vigente" sem fixar onde ele vive. Se a API
 * devolvesse a etapa pronta, ela viraria contrato — e quando o `NR-24`
 * trouxer substatus configurável por operadora, mudar o mapa quebraria o
 * contrato. Aqui é trocar a fonte deste arquivo (`FC-1` do `TP-043`).
 */

import { type EstadoDoCaso, ROTULOS_DE_ESTADO } from './api/estado';

export type Etapa = 'novo' | 'avaliacao' | 'orcamento' | 'operadora';

/** `'desconhecida'` é o `F4`: estado sem etapa APARECE, nunca some da tela. */
export type EtapaResolvida = Etapa | 'desconhecida';

export const ROTULOS_DE_ETAPA: Record<EtapaResolvida, string> = {
  novo: 'Novo',
  avaliacao: 'Avaliação',
  orcamento: 'Orçamento',
  operadora: 'Operadora',
  desconhecida: 'Sem etapa conhecida',
};

/** As quatro, na ordem do painel. `desconhecida` fica fora: não é etapa, é aviso. */
export const ETAPAS: readonly Etapa[] = ['novo', 'avaliacao', 'orcamento', 'operadora'];

/**
 * `Record<EstadoDoCaso, …>` não é enfeite: se a API ganhar um oitavo estado,
 * a próxima geração do contrato quebra ESTE objeto no `tsc`, que é onde a
 * quebra custa menos. `null` é o estado que o painel não mostra de propósito
 * — `encerrado` já saiu da captação (`S2`).
 */
const ETAPA_POR_ESTADO: Record<EstadoDoCaso, Etapa | null> = {
  Received: 'novo',
  UnderAssessment: 'avaliacao',
  QuoteIssued: 'orcamento',
  AwaitingPayer: 'operadora',
  Authorized: 'operadora',
  Declined: 'operadora',
  Closed: null,
};

/**
 * A etapa de um estado.
 *
 * `null` = fora do painel por decisão (`encerrado`). `'desconhecida'` = o mapa
 * não cobre o estado, e aí o Caso aparece sinalizado — esconder faria a tela
 * afirmar que o Caso não existe, e quem procurasse voltaria ao IW, que é o
 * que o painel existe para evitar.
 */
export function etapaDoEstado(estado: string): EtapaResolvida | null {
  if (!Object.hasOwn(ETAPA_POR_ESTADO, estado)) return 'desconhecida';
  return ETAPA_POR_ESTADO[estado as EstadoDoCaso];
}

/**
 * Que relógio vale em cada estado (`S3`, `S4`, `S5` da `BR-006`).
 *
 * `decorrido` é o vão que a `BR-005` deixa: o relógio de resposta PARA na
 * entrega à operadora e o de implantação só nasce na autorização. Entre os
 * dois não há prazo nosso — mostrar tempo decorrido diz a verdade; deixar o
 * de resposta correndo marcaria como atraso da Anery uma espera da operadora.
 */
export type RelogioDaEtapa = 'resposta' | 'decorrido' | 'implantacao' | 'nenhum';

const RELOGIO_POR_ESTADO: Record<EstadoDoCaso, RelogioDaEtapa> = {
  Received: 'resposta',
  UnderAssessment: 'resposta',
  QuoteIssued: 'resposta',
  AwaitingPayer: 'decorrido',
  Authorized: 'implantacao',
  Declined: 'nenhum',
  Closed: 'nenhum',
};

export function relogioDoEstado(estado: string): RelogioDaEtapa {
  if (!Object.hasOwn(RELOGIO_POR_ESTADO, estado)) return 'nenhum';
  return RELOGIO_POR_ESTADO[estado as EstadoDoCaso];
}

/** O rótulo do estado, para o cartão. Reusa o vocabulário da tela de Casos. */
export function rotuloDoEstado(estado: string): string {
  return Object.hasOwn(ROTULOS_DE_ESTADO, estado)
    ? ROTULOS_DE_ESTADO[estado as EstadoDoCaso]
    : estado;
}
